import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async"; // NEW: Required for SEO
import PageSeo from "./components/SEO/PageSeo"; // NEW: Your custom SEO component

// Preloader & Navbar imports
import Preloader from "./components/Preloader/Preloader";
import Navbar from "./components/Navbar/Navbar";

// Component imports (Keeping your exact existing imports)
import MarketplacePage from "./components/Marketplace/MarketplacePage";
import PostLoadPage from "./components/Company/CompanyPages/PostLoadPage";
import TrustStrip from "./components/Pages/TrustStrip";
import PricingPage from "./components/Pages/PricingPage";
import MembershipTeaser from "./components/Membership/Membership";
import TrainingPage from "./components/Training/TrainingHub";
import CTA from "./components/CTA/CTA";
import DisclaimerPage from "./components/Legal/Disclaimer";
import MyLoads from "./components/Company/CompanyPages/MyLoads";
import VerifiedDriversPage from "./components/Company/CompanyPages/VerifiedCompanyDriversPage";
import Analytics from "./components/Company/CompanyPages/Analytics";
import CrashCourseView from "./components/CrashCourse/CrashCourseView";
import StateInfographic from "./components/CrashCourse/StateInfographic";
import PostExamVerification from "./components/CrashCourse/PostExamVerification";
import PPCAWashingtonStatesCrashCourse from "./components/CrashCourse/PPCAWashingtonStatesCrashCourse";
import PPCAVerificationTab from "./components/CrashCourse/PPCAVerificationTab";
import DriverVerificationBadge from "./components/Verified/PilotLinkConsole";
import DispatcherConsole from "./components/Verified/DispatcherConsole";
import PPCAAllStatesCrashCourse from "./components/CrashCourse/PPCAAllStatesCrashCourse";
import CertificateOfCompletion from "./components/CrashCourse/FinalExam/CertificateOfCompletion";
import PilotLogin from "./components/Auth/PilotLogin";
import CompanyLogin from "./components/Auth/CompanyLogin";
import DispatcherLogin from "./components/Auth/DispatcherLogin";
import Signup from "./components/Auth/SignupTemp";
import Footer from "./components/Footer/Footer";
import Hero from "./components/Hero/HeroPage";
import BecomePartner from "./components/Hero/BecomePartnerPage";
import PartnerPreviewPage from "./components/Hero/PartnerPreviewPage";
import SetupBilling from "./components/SetupBilling/SetupBilling";

// Test imports
import TestPilotMasterv2 from "./TestPages/TestPilotMasterv2";
import TestPPCAStudyGuide from "./TestPages/TestPPCAStudyGuide";
import TestUnifiedDashboard from "./TestPages/TestUnifiedDashboard";
import TestPPCAVerifyChecklist from "./TestPages/TestPPCAVerifyChecklist";
import TestDispatcherConsole from "./TestPages/TestDispatcherConsole";
import TestCertVerify from "./TestPages/TestCertVerifyPage";

// Dashboard & Driver imports
import DriverDashboardPage from "./components/Driver/DriverDashboard";
import OverviewPage from "./components/Driver/DriverPages/Overview";
import LoadBoardPage from "./components/Driver/DriverPages/LoadBoardPage";
import SavedLoadsPage from "./components/Driver/DriverPages/SavedLoads";
import InvoicesPage from "./components/Driver/DriverPages/Invoices";
import MyJobs from "./components/Driver/DriverPages/MyJobs";
import JobConsolePage from "./components/Driver/DriverPages/JobConsolePage";
import CompanyDashboardPage from "./components/Company/CompanyDashboard";
import LoadApplicantsPage from "./components/Company/CompanyPages/LoadApplicantsPage";
import DispatchersPage from "./components/Company/CompanyPages/DispatchersPage";

// Legal & Marketplace imports
import PrivacyPolicy from "./components/Legal/PrivacyPolicy";
import CopyrightNotice from "./components/Legal/CopyrightNotice";
import TermsOfService from "./components/Legal/TermsOfService";
import RefundPolicy from "./components/Legal/RefundPolicy";
import ContactUs from "./components/Legal/ContactUs";
import PostListing from "./components/Marketplace/PostListing";
import TradeLinkInbox from "./components/Marketplace/TradeLinkInbox/TradeLinkInbox";
import TradeLinkDashboard from "./components/Marketplace/TradeLinkDashboard";
import ScrollToTop from "./components/Pages/ScrollToTop";
import TradeLinkItemsManager from "./components/MarketplaceTradelinkAdmin/TradeLinkItemsManager";

// Dispatcher Dashboard & Others
import DispatcherDashboard from "./components/Dispatcher/DispatcherDashboard";
import DispatcherOverview from "./components/Dispatcher/Pages/DispatcherOverview";
import DispatcherOrders from "./components/Dispatcher/Pages/DispatcherOrders";
import NotFound from "./components/NotFound/404NotFound";
import ForgotPassword from "./components/Auth/ForgotPassword";
import ResetPassword from "./components/Auth/ResetPassword";
import CheatSheetVault from "./components/CrashCourse/CheatSheetVault";
import PeterQAWidget from "./TestPages/PeterQAWidget";
import CompanyAddOn from "./TestPages/CompanyAddOn";
import StarterBundleLanding from "./TestPages/StarterBundleLanding";
import DriverSettings from "./components/Driver/DriverPages/DriverSettings";
import ProductDetailPage from "./components/Marketplace/ProductDetailPage";


export default function App() {
  const [loading, setLoading] = useState(true);
  const defaultTheme = process.env.REACT_APP_DEFAULT_THEME || "light";

  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return defaultTheme;
    const savedTheme = localStorage.getItem("theme");
    return savedTheme ? savedTheme : defaultTheme;
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const root = window.document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(theme === "light" ? "dark" : "light");
  };

  if (loading) {
    return <Preloader />;
  }

  const LandingPage = () => (
    <>
      {/* NEW: SEO metadata specifically for the Landing Page */}
      <PageSeo 
        title="Pilot Car Network & Heavy Haul Escort Services"
        description="Join PilotLink USA to connect with certified pilot car drivers, escort vehicles, and oversize load dispatchers nationwide."
        canonicalUrl="/"
      />
      <Navbar isDarkMode={theme === "dark"} toggleTheme={toggleTheme} />
      <main>
        <Hero isDarkMode={theme === "dark"} />
        <TrustStrip isDarkMode={theme === "dark"} />
        <MembershipTeaser isDarkMode={theme === "dark"} />
        <CTA isDarkMode={theme === "dark"} />
      </main>
      <PeterQAWidget/>
      <Footer />
      <ScrollToTop />
    </>
  );

  return (
    // NEW: Wrap your entire app in HelmetProvider so React Helmet Async works
    <HelmetProvider>
      <div className="min-h-screen bg-slate-50 text-slate-900 transition-colors duration-500 dark:bg-slate-950 dark:text-white">
        <Router>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/marketplace" element={<MarketplacePage isDarkMode={theme === "dark"} toggleTheme={toggleTheme} />} />
            <Route path="/marketplace/listing/:id" element={<ProductDetailPage isDarkMode={theme === "dark"} toggleTheme={toggleTheme} />} />

            <Route path="/post-load" element={<PostLoadPage isDarkMode={theme === "dark"} toggleTheme={toggleTheme} />} />
            <Route path="/training" element={<TrainingPage isDarkMode={theme === "dark"} toggleTheme={toggleTheme} />} />
            <Route path="/pricing" element={<PricingPage isDarkMode={theme === "dark"} toggleTheme={toggleTheme} />} />
            <Route path="/disclaimer" element={<DisclaimerPage isDarkMode={theme === "dark"} toggleTheme={toggleTheme} />} />
            
            <Route path="/login" element={<PilotLogin />} />
            <Route path="/company-login" element={<CompanyLogin />} />
            <Route path="/dispatcher-login" element={<DispatcherLogin />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/become-partner" element={<BecomePartner />} />
            <Route path="/partner-preview" element={<PartnerPreviewPage />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/setup-billing" element={<SetupBilling />} />

            <Route element={<DriverDashboardPage />}>
              <Route path="/user-dashboard" element={<OverviewPage />} />
              <Route path="/my-jobs" element={<MyJobs />} />
              <Route path="/load-board" element={<LoadBoardPage />} />
              <Route path="/saved-loads" element={<SavedLoadsPage />} />
              <Route path="/invoices" element={<InvoicesPage />} />
              <Route path="/my-jobs/console/:id" element={<JobConsolePage />} />
              <Route path="/settings" element={<DriverSettings />} />
            </Route>

            <Route path="/company-dashboard" element={<CompanyDashboardPage />} />
            <Route path="/loads/:loadId/applicants" element={<LoadApplicantsPage />} />
            <Route path="/my-loads" element={<MyLoads />} />
            <Route path="/verified-drivers" element={<VerifiedDriversPage />} />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/dispatchers" element={<DispatchersPage />} />

            <Route element={<DispatcherDashboard />}>
              <Route path="/dispatcher-dashboard" element={<DispatcherOverview />} />
              <Route path="/dispatcher-orders" element={<DispatcherOrders />} />
            </Route>

            <Route path="/training/crash-course" element={<PPCAAllStatesCrashCourse isDarkMode={theme === "dark"} toggleTheme={toggleTheme} />} />
            <Route path="/training/crash-course/study" element={<CrashCourseView />} />
            <Route path="/training/crash-course/state-infographic" element={<StateInfographic />} />
            <Route path="/training/crash-course/post-exam-verification" element={<PostExamVerification />} />
            <Route path="/training/all-states-cheat-sheet" element={<CheatSheetVault />} />
            <Route path="/training/ppca-all-states-crash-course" element={<PPCAAllStatesCrashCourse isDarkMode={theme === "dark"} toggleTheme={toggleTheme} />} />
            <Route path="/training/ppca-certificate-of-completion" element={<CertificateOfCompletion isDarkMode={theme === "dark"} toggleTheme={toggleTheme} />} />
            <Route path="/training/ppca-washington-states-crash-course" element={<PPCAWashingtonStatesCrashCourse isDarkMode={theme === "dark"} toggleTheme={toggleTheme} />} />
            <Route path="/training/ppca-verificationtable" element={<PPCAVerificationTab isDarkMode={theme === "dark"} toggleTheme={toggleTheme} />} />
            
            <Route path="/pilot-user-verification-badge" element={<DriverVerificationBadge isDarkMode={theme === "dark"} toggleTheme={toggleTheme} />} />
            <Route path="/dispatcher-console" element={<DispatcherConsole isDarkMode={theme === "dark"} toggleTheme={toggleTheme} />} />
            
            <Route path="/test-companyaddon" element={<CompanyAddOn />} />
            <Route path="/test-pilotv2" element={<TestPilotMasterv2 />} />
            <Route path="/test-ppca-doc-verification" element={<TestPPCAVerifyChecklist />} />
            <Route path="/test-ppcastudyguide" element={<TestPPCAStudyGuide />} />
            <Route path="/test-dispatcherconsole" element={<TestDispatcherConsole />} />
            <Route path="/test-unifieddashboard" element={<TestUnifiedDashboard />} />
            <Route path="/test-certverify" element={<TestCertVerify />} />
            <Route path="/test-starterbundle" element={<StarterBundleLanding />} />

            <Route path="/tradelink/post-listing" element={<PostListing />}/>
            <Route path="/tradelink/inbox" element={<TradeLinkInbox />}/>
            <Route path="/tradelink/inbox1" element={<TradeLinkDashboard />}/>
            <Route path="/admin/tradelink/items-manager" element={<TradeLinkItemsManager />}/>

            <Route path="/copyright" element={<CopyrightNotice />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/contact" element={<ContactUs />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Router>
      </div>
    </HelmetProvider>
  );
}